import AlertPopup from "components/alert-popup/AlertPopup";
import { FIELD_REQUIRED } from "constants/popup-codes";
import Button from "core/buttons/button/Button";
import ButtonImportMission from "core/buttons/button-import-mission/ButtonImportMission";
import ImportMissionModal from "components/form/mission/import-modal/ImportMissionModal";
import InfoContext from "pages/InfoContext";
import InfoHeaderAction from "pages/info-header/InfoHeaderAction";
import PastDateWarningModal from "components/modal/PastDateWarningModal";
import ValidationModal from "components/modal/ValidationModal";
import _ from "lodash";
import { ENTITY } from "constants/info-header";
import { FormattedMessage } from "react-intl";
import { MISSION_DEFAULT_VALUES } from "constants/forms";
import {
  CLIENT_TYPES,
  POPUP_STATUS,
  QUERY_STATUS,
  SECTIONS,
  TAG_TYPES,
  FILTERS_FOR_NAMES,
} from "constants/constants";
import { switchDataNames } from "constants/MissionSwitchData";
import { useAppealContext } from "api/settings/appeal/AppealProvider";
import { useBranchContext } from "api/settings/branch/BranchProviders";
import { useClientContext } from "api/directory/client/ClientProvider";
import { useCountryContext } from "api/settings/country/CountryProvider";
import { useCurrencyContext } from "api/settings/currency/CurrencyProvider";
import { useDybContext } from "api/external-apis/dyb/DybProvider";
import { useForm } from "react-hook-form";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { useJobContext } from "api/settings/job/JobProvider";
import { useMemo, useEffect, useState, useRef } from "react";
import { useMissionContext } from "api/mission/MissionProvider";
import { usePersonContext } from "api/directory/person/PersonProvider";
import { useSettingsContext } from "api/settings/SettingsProvider";
import { useStaffContext } from "api/directory/staff/StaffProvider";
import { getStaffByIdQuery } from "api/directory/staff/queries";
import { useUserContext } from "api/user/UserProvider";
import {
  clearManagerBranches,
  formatImportedData,
  formatPayloadToAdd,
  updateManagerBranches,
  determineSectionForErrors,
  handleSwitchDataValidation,
  getGeneralInfoRequiredFields,
  getBillingRequiredFields,
  validateAllRequiredFields,
  handleMissionValidationErrors,
  submitMissionCreation,
} from "utils/missionHelper";
import MissionGeneralInfo from "components/sections-mission/MissionGeneralInfo";
import MissionBillingInfo from "components/sections-mission/MissionBillingInfo";
import { useNavigate } from "react-router-dom";
import DetachmentMissionModal from "components/modal/DetachmentMissionModal";
import colors from "assets/styles/mixins/colors.scss";
import moment from "moment";

export const CreateMission = () => {
  const { lang, userScope, apiErrorResponse, setApiErrorResponse } =
    useUserContext();
  const {
    branchListGetter,
    branchList,
    getAssignedBranchWithoutScope,
    staffAssignedBranchWithoutScope,
  } = useBranchContext();
  const { jobList, jobListGetter } = useJobContext();
  const {
    getPersonsByProfile,
    personsByProfileList,
    personsList,
    getPersonsByIds,
    isManagerOfPerson,
    actualManager,
  } = usePersonContext();
  const { searchUsersByName, usersByName } = useDybContext();
  const {
    sirenErrorMessage,
    setSirenErrorMessage,
    getNamesByFilters,
    namesByFilters,
    searchUsers,
    usersByIds,
  } = useGeneralContext();
  const {
    getMissionsByWorkerId,
    workerMissionsList,
    getMissionsByFilters,
    missionsByFilters,
    createMission,
    missionCreatedStatus,
    missionCreated,
    setMissionCreated,
    setDeleteMissionStatus,
  } = useMissionContext();
  const { getStaffById, staffById } = useStaffContext();
  const {
    getClientsByIds,
    clientsByIds,
    getAssignedClients,
    assignedClients,
    getClientById,
    clientById,
  } = useClientContext();
  const { appealList, appealListGetter } = useAppealContext();
  const { getDataTTV, dataTTV } = useSettingsContext();
  const [isOpenImportModal, setIsOpenImportModal] = useState(false);
  const { currencyList, currencyListGetter } = useCurrencyContext();
  const [data, setData] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [section, setSection] = useState(SECTIONS.GENERAL_INFO);
  const [currencyType, setCurrencyType] = useState("€");
  const [managerBranches, setManagerBranches] = useState([]);
  const [managerContractBranchId, setManagerContractBranchId] =
    useState(undefined);
  const managerContractFetchIdRef = useRef(0);
  const [countryCurrencies, setCountryCurrencies] = useState([]);
  const [changedActiveSection, setChangedActiveSection] = useState();
  const [sectionChangeCounter, setSectionChangeCounter] = useState(0);
  const [code, setCode] = useState();
  const [status, setStatus] = useState();
  const { countryByIdGetter, countryPerId } = useCountryContext();
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
    clearErrors,
    reset,
    unregister,
    setError,
    getValues,
    setErrors,
  } = useForm({
    defaultValues: {
      ...MISSION_DEFAULT_VALUES,
    },
  });
  const [isValidated, setIsValidated] = useState(false);
  const [cellHighlight, setCellHighlight] = useState(false);

  const managerId = watch("manager_id");
  const personId = watch("worker_id");

  const navigate = useNavigate();
  const chargedBy = watch("charged_by_id");

  const [missingCharValue, setMissingCharValue] = useState(0);
  const [errorNameMessage, setErrorNameMessage] = useState("");
  const [errorWordDescriptionMessage, setErrorWordDescriptionMessage] =
    useState("");
  const [descriptionMessageWordValue, setDescriptionMessageWordValue] =
    useState(50);
  const [errorDashDescriptionMessage, setErrorDashDescriptionMessage] =
    useState("");
  const [descriptionMessageDashValue, setDescriptionMessageDashValue] =
    useState(0);
  const [dataPayload, setDataPayload] = useState({});
  const [isDetachmentModalOpen, setIsDetachmentModalOpen] = useState(false);
  const [pendingValidation, setPendingValidation] = useState(false);
  const [isPastStartDateWarningOpen, setIsPastStartDateWarningOpen] = useState(false);
  const pendingSubmitInfoRef = useRef(null);
  // Synchronous lock: blocks re-entrant submits before React re-renders the disabled button
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    isValidated && errors && setCellHighlight(true);
  }, [errors, isValidated]);

  useEffect(() => {
    setApiErrorResponse(null);
  }, []);

  useEffect(() => {
    if (apiErrorResponse) {
      Object.entries(apiErrorResponse).forEach(([field, message]) => {
        setError(field, { type: "manual", message });
      });
    }
  }, [apiErrorResponse, setError]);

  useEffect(() => {
    if (!pendingValidation) return;

    const generalInfoFields = getGeneralInfoRequiredFields(dataTTV);
    const billingFields = getBillingRequiredFields();

    generalInfoFields.forEach(({ field, check }) => {
      if (check(watch)) {
        setError(field, {
          type: "manual",
          message: "Ce champ est obligatoire",
        });
      }
    });

    billingFields.forEach(({ field, check }) => {
      if (check(watch)) {
        setError(field, {
          type: "manual",
          message: "Ce champ est obligatoire",
        });
      }
    });

    setPendingValidation(false);
    setCellHighlight(true);
  }, [section, pendingValidation, dataTTV]);

  useEffect(() => {
    setDeleteMissionStatus(QUERY_STATUS.INITIAL);

    const mascaradeUserId = localStorage.getItem("connected_as");
    const userId = localStorage.getItem("id");
    const firstName = localStorage.getItem("first_name");
    const lastName = localStorage.getItem("last_name");
    setValue("manager_id", [
      {
        value: mascaradeUserId ? mascaradeUserId : userId,
        type: TAG_TYPES.PEOPLE,
        label: `${firstName} ${lastName}`,
      },
    ]);
    if (mascaradeUserId && mascaradeUserId !== userId) {
      getNamesByFilters({
        types: [FILTERS_FOR_NAMES.PERSON],
        id: [mascaradeUserId],
      });
    } else if (userId) {
      getNamesByFilters({
        types: [FILTERS_FOR_NAMES.PERSON],
        id: [userId],
      });
    }
  }, []);

  useEffect(() => {
    if (managerId[0]?.value) {
      getNamesByFilters({
        types: [FILTERS_FOR_NAMES.PERSON],
        id: [managerId[0].value],
      });
    } else {
      setValue("signed_by_id", []);
      setManagerBranches([]);
    }
  }, [setValue, managerId]);

  useEffect(() => {
    if (managerId?.[0]?.value && namesByFilters?.length > 0)
      getAssignedBranchWithoutScope(Number(managerId[0].value));
  }, [managerId, namesByFilters]);

  useEffect(() => {
    const currentManagerId = managerId?.[0]?.value;
    if (!currentManagerId) {
      setManagerContractBranchId(null);
      return;
    }
    setManagerContractBranchId(undefined);
    const fetchId = ++managerContractFetchIdRef.current;
    (async () => {
      try {
        const result = await getStaffByIdQuery({
          id: currentManagerId,
          userScope,
        });
        if (fetchId === managerContractFetchIdRef.current) {
          setManagerContractBranchId(result?.contract?.branch_id ?? null);
        }
      } catch (e) {
        console.error("Fetch manager contract for signed_by default", e);
        if (fetchId === managerContractFetchIdRef.current) {
          setManagerContractBranchId(null);
        }
      }
    })();
  }, [managerId, userScope]);

  useEffect(() => {
    const hasValidManager = managerId?.[0]?.value;
    const hasBranchData =
      staffAssignedBranchWithoutScope &&
      Object.keys(staffAssignedBranchWithoutScope).length > 0;
    const hasManagerData = namesByFilters?.length > 0;
    const hasContractResolution = managerContractBranchId !== undefined;

    if (
      hasValidManager &&
      hasBranchData &&
      hasManagerData &&
      hasContractResolution
    ) {
      updateManagerBranches({
        managerContractBranchId,
        staffAssignedBranchWithoutScope,
        branchList,
        setManagerBranches,
        setValue,
      });
    } else if (managerId?.length === 0) {
      clearManagerBranches({
        setValue,
        setManagerBranches,
      });
    }
  }, [
    managerId,
    staffAssignedBranchWithoutScope,
    namesByFilters,
    branchList,
    managerContractBranchId,
  ]);

  useEffect(() => {
    if (chargedBy.length > 0) {
      const branchCountry = branchList?.find(
        (elm) => elm?.id === chargedBy[0]?.value
      );
      if (branchCountry) countryByIdGetter(branchCountry?.country_id);
    }
  }, [chargedBy]);

  useEffect(() => {
    if (countryPerId?.id) {
      setCountryCurrencies(
        currencyList.filter((elm) =>
          countryPerId?.currencies?.includes(elm?.id)
        )
      );
      setValue("currency_id", countryPerId?.default_cur);
      if (errors.currency_id) clearErrors("currency_id");
    }
  }, [countryPerId]);

  const getOptions = (search, profile = "") => {
    getPersonsByProfile({ search, profile });
  };

  const changeSection = (targetSection) => {
    setSection(targetSection);
    setSectionChangeCounter((prev) => prev + 1);
    setChangedActiveSection({
      section: targetSection,
      key: sectionChangeCounter + 1,
    });
  };

  const proceedWithCreation = async (info) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      await submitMissionCreation({
        info,
        managerId,
        personId,
        setDataPayload,
        isManagerOfPerson,
        formatPayloadToAdd,
        createMission,
        setIsOpen,
        setIsDetachmentModalOpen,
      });
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const onSubmit = async (info) => {
    const { hasSwitchErrors, hasGeneralInfoErrors, hasBillingErrors } =
      validateAllRequiredFields({ watch, setError, switchDataNames, dataTTV });

    if (hasSwitchErrors || hasGeneralInfoErrors || hasBillingErrors) {
      handleMissionValidationErrors({
        hasSwitchErrors,
        hasGeneralInfoErrors,
        setStatus,
        setCode,
        changeSection,
        setPendingValidation,
        POPUP_STATUS,
        FIELD_REQUIRED,
        SECTIONS,
      });
      return;
    }

    if (
      info.start_date &&
      moment(info.start_date).isBefore(moment().startOf("day"))
    ) {
      pendingSubmitInfoRef.current = info;
      setIsPastStartDateWarningOpen(true);
      return;
    }

    await proceedWithCreation(info);
  };

  const handlePastStartDateContinue = async () => {
    setIsPastStartDateWarningOpen(false);
    if (pendingSubmitInfoRef.current) {
      await proceedWithCreation(pendingSubmitInfoRef.current);
      pendingSubmitInfoRef.current = null;
    }
  };

  const handlePopupConfirm = () => {
    createMission(
      formatPayloadToAdd({
        data: dataPayload,
        isDetached: true,
      })
    );
    setIsOpen(true);
    setIsDetachmentModalOpen(false);
  };

  const handlePopupCancel = () => {
    createMission(
      formatPayloadToAdd({
        data: dataPayload,
        isDetached: false,
      })
    );
    setIsOpen(true);
    setIsDetachmentModalOpen(false);
  };

  const copyMission = (val) => {
    if (val?.worker_id) {
      const isClientAccessible = assignedClients?.some(
        (c) => c.id === val?.client_id
      );
      const dataToImport = { ...val };

      if (!isClientAccessible) {
        const clientFields = [
          "email",
          "contact_name",
          "client_contact",
          "client_contact_phonenumber",
          ...(val?.isDeletedClient
            ? [
                "client_id",
                "client",
                "address",
                "client_contact_email",
                "client_contact_linkedin",
              ]
            : []),
        ];
        clientFields.forEach((field) => delete dataToImport[field]);
      }
      setData(dataToImport);
      setIsOpenImportModal(false);
    }
  };

  useEffect(() => {
    branchListGetter();
    jobListGetter();
    currencyListGetter();
    appealListGetter();
    getDataTTV();
  }, [
    jobListGetter,
    branchListGetter,
    currencyListGetter,
    appealListGetter,
    getDataTTV,
  ]);

  const onError = (formErrors) => {
    handleSwitchDataValidation(watch, setError, switchDataNames);

    const hasSwitchErrors = switchDataNames.some(
      (switchName) => watch(switchName) === null
    );

    if (!_.isEmpty(formErrors) || hasSwitchErrors) {
      setStatus(POPUP_STATUS.ERROR);
      setCode(FIELD_REQUIRED);
      setCellHighlight(true);

      let targetSection;
      if (hasSwitchErrors) {
        targetSection = SECTIONS.GENERAL_INFO;
      } else {
        targetSection = determineSectionForErrors({
          errors: formErrors,
          watch,
          SECTIONS,
          dataTTV,
        });
      }

      if (targetSection) {
        changeSection(targetSection);
      }
    }
  };

  useEffect(() => {
    if (data?.id) {
      const formattedData = formatImportedData(data, {
        lang,
        davFunctions: jobList,
        usersByIds: personsList,
        branches: branchList,
        namesByFilters: namesByFilters,
      });
      Object.keys(formattedData).forEach((elm) => {
        setValue(elm, formattedData[elm]);
      });
      setValue("worker_id", [
        {
          value: data?.worker_id[0]?.value,
          type: TAG_TYPES.PEOPLE,
          label: data?.worker_id[0]?.label,
        },
      ]);
      if (!data?.client_id) {
        setValue("client_id", []);
      }

      if (data?.manager_id) {
        getNamesByFilters({
          types: [FILTERS_FOR_NAMES.PERSON],
          id: [data.manager_id],
        });
      }
    }
  }, [data]);

  const switchSection = () => {
    if (section === SECTIONS.BILLING) {
      changeSection(SECTIONS.GENERAL_INFO);
    } else {
      changeSection(SECTIONS.BILLING);
    }
  };

  const resetForm = () => {
    setCellHighlight(false);
    setIsValidated(false);
    reset(MISSION_DEFAULT_VALUES);
    setData({});
    window.scrollTo(0, 0);
  };

  const values = useMemo(
    () => ({
      getNamesByFilters,
      namesByFilters,
      countryCurrencies,
      getClientById,
      clientById,
      errorDashDescriptionMessage,
      setErrorDashDescriptionMessage,
      descriptionMessageDashValue,
      setDescriptionMessageDashValue,
      countryPerId,
      missingCharValue,
      setMissingCharValue,
      errorNameMessage,
      setErrorNameMessage,
      errorWordDescriptionMessage,
      setErrorWordDescriptionMessage,
      descriptionMessageWordValue,
      setDescriptionMessageWordValue,
      isValidated,
      cellHighlight,
      setCellHighlight,
      staffById,
      getStaffById,
      currencyList,
      copyMission: copyMission,
      managerBranches,
      branches: branchList,
      davFunctions: jobList,
      lang,
      personsList,
      personsByProfileList,
      getOptions,
      userScope,
      sirenErrorMessage,
      setSirenErrorMessage,
      usersByName,
      getMissionsByWorkerId,
      workerMissionsList,
      getMissionsByFilters,
      missionsByFilters,
      getClientsByIds,
      clientsByIds,
      appealList,
      getClients: ({ search }) =>
        getAssignedClients({
          search,
          clientTypes: [CLIENT_TYPES.BRANCH],
        }),
      getPersonsByName: ({ search }) => searchUsersByName({ search }),
      clientsList: assignedClients,
      searchUsers,
      usersByIds,
      getDataTTV,
      dataTTV,
      data,
      section,
      currencyType,
      setCurrencyType,
      control,
      errors,
      handleSubmit,
      register,
      setValue,
      watch,
      clearErrors,
      reset,
      unregister,
      setErrors,
      setError,
      changedActiveSection,
      getPersonsByIds,
      apiErrorResponse,
      entity: ENTITY.MISSION,
      isEnabled: true,
      readOnly: false,
      setIsEnabled: () => {},
      setReadOnly: () => {},
      setSection: setSection,
      setMissionCreated,
      isAuthorizedManager: clientById?.assigned_managers?.includes(
        Number(managerId[0]?.value)
      ),
    }),
    [
      getNamesByFilters,
      namesByFilters,
      countryCurrencies,
      getClientById,
      clientById,
      errorDashDescriptionMessage,
      setErrorDashDescriptionMessage,
      descriptionMessageDashValue,
      setDescriptionMessageDashValue,
      countryPerId,
      missingCharValue,
      setMissingCharValue,
      errorNameMessage,
      setErrorNameMessage,
      errorWordDescriptionMessage,
      setErrorWordDescriptionMessage,
      descriptionMessageWordValue,
      setDescriptionMessageWordValue,
      isValidated,
      cellHighlight,
      staffById,
      getStaffById,
      currencyType,
      setCurrencyType,
      section,
      setSection,
      data,
      copyMission,
      lang,
      branchList,
      sirenErrorMessage,
      setSirenErrorMessage,
      jobList,
      personsList,
      personsByProfileList,
      userScope,
      getOptions,
      currencyList,
      usersByName,
      searchUsersByName,
      getMissionsByWorkerId,
      workerMissionsList,
      getMissionsByFilters,
      missionsByFilters,
      getClientsByIds,
      clientsByIds,
      appealList,
      getAssignedClients,
      assignedClients,
      searchUsers,
      usersByIds,
      getDataTTV,
      dataTTV,
      getPersonsByIds,
      changedActiveSection,
      apiErrorResponse,
      managerId,
    ]
  );

  return (
    <>
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <DetachmentMissionModal
        open={isDetachmentModalOpen && isValidated}
        onCloseModal={() => setIsDetachmentModalOpen(false)}
        onClose={() => handlePopupCancel()}
        onValidate={() => handlePopupConfirm()}
        title={{
          id: "manage_mission_detachment",
          defaultMessage: "Détachement définitif",
        }}
        data={{
          cslt_str: personId?.[0]?.label || "",
          manager: `${actualManager?.manager?.first_name} ${actualManager?.manager?.last_name}`,
        }}
      />
      <PastDateWarningModal
        open={isPastStartDateWarningOpen}
        onContinue={handlePastStartDateContinue}
        onClose={() => setIsPastStartDateWarningOpen(false)}
        messageId="modal_past_start_date_mission_warning_msg"
      />
      <ValidationModal
        open={isOpen}
        status={missionCreatedStatus}
        onClose={() => {
          setIsOpen(false);
          changeSection(SECTIONS.GENERAL_INFO);
          if (missionCreatedStatus === QUERY_STATUS.SUCCESS) {
            return navigate(`/${userScope}/missions/${missionCreated?.slug}`);
          }
        }}
        messages={{
          pendingTitleId: "modal_validation_create_file",
          pendingMessageId: "modal_validation_file_pending_msg",
          successTitleId: "modal_validation_create_file",
          successMessageId: "modal_validation_file_success_msg",
          errorTitleId: "modal_validation_create_file",
          errorMessageId: "modal_validation_field_errors_msg",
          errorMessageDefault:
            "Certains champs comportent des erreurs.{br}Veuillez cliquer sur les <nowrap>icônes{errorIcon}pour</nowrap> obtenir plus de détails et corriger les informations nécessaires.",
        }}
      />
      <InfoContext.Provider value={values}>
        <InfoHeaderAction />
        <ButtonImportMission
          onClick={() => setIsOpenImportModal(!isOpenImportModal)}
          isOpenModal={isOpenImportModal}
        >
          import
        </ButtonImportMission>
        <ImportMissionModal
          open={isOpenImportModal}
          onClose={() => {
            setIsOpenImportModal(false);
          }}
          setIsOpen={() => setIsOpenImportModal(!isOpenImportModal)}
        />
        <form
          onSubmit={(e) => {
            clearErrors();
            handleSubmit(onSubmit, onError)(e);
          }}
        >
          {section === SECTIONS.GENERAL_INFO && (
            <MissionGeneralInfo
              clearErrors={clearErrors}
              control={control}
              setError={setError}
              errors={errors}
              register={register}
              setErrors={setErrors}
              getValues={getValues}
              setValue={setValue}
              unregister={unregister}
              watch={watch}
              code={code}
              getCode={setCode}
              getStatus={setStatus}
            />
          )}
          {section === SECTIONS.BILLING && (
            <MissionBillingInfo
              clearErrors={clearErrors}
              control={control}
              errors={errors}
              register={register}
              setValue={setValue}
              unregister={unregister}
              watch={watch}
              code={code}
              getCode={setCode}
              getStatus={setStatus}
              readOnly={false}
            />
          )}
          <div className="mission-form-buttons">
            {section === SECTIONS.BILLING ? (
              <div className="beforeButton">
                <Button
                  className="btn-standard btn-outline"
                  onClick={switchSection}
                  type="button"
                  id="button-form-next"
                >
                  <FormattedMessage id="before" defaultMessage="Précédent" />
                </Button>
              </div>
            ) : (
              <div />
            )}
            <div className="afterButton">
              <Button
                className="btn-standard btn-outline"
                onClick={resetForm}
                type="button"
                id="button-form-reset"
              >
                <FormattedMessage id="cancel" defaultMessage="Annuler" />
              </Button>
              {section === SECTIONS.GENERAL_INFO ? (
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    switchSection();
                  }}
                  color="btn-medium gray-700"
                  type="button"
                  id="button-form-next"
                >
                  <FormattedMessage id="next" defaultMessage="Suivant" />
                </Button>
              ) : (
                <Button
                  onClick={() => setIsValidated(true)}
                  color="btn-medium gray-700"
                  id="button-form-validation"
                  backgroundColor={
                    missionCreatedStatus === QUERY_STATUS.LOADING &&
                    colors.disabledGrey
                  }
                  disabled={missionCreatedStatus === QUERY_STATUS.LOADING}
                >
                  <FormattedMessage
                    id="button_form_validation"
                    defaultMessage="Valider la création"
                  />
                </Button>
              )}
            </div>
          </div>
        </form>
      </InfoContext.Provider>
    </>
  );
};
